/**
 * RedownloadButton - Button for re-downloading images
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { downloadWithFingerprint } from "@/lib/api";
import { trackMetaEvent } from "@/lib/meta";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";

export function RedownloadButton({ publicId, dishName, variant = "icon" }) {
  const [loading, setLoading] = useState(false);

  const handleRedownload = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      // Use fingerprint-enabled download, preview_opened = false (re-download without preview)
      const res = await downloadWithFingerprint(publicId, false);
      const { download_url, filename, meta_event_id, meta_event_name } = res.data;
      const link = document.createElement("a");
      link.href = download_url;
      link.download = filename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Downloading ${dishName}`);
      // Fire download tracking event if present (won't fire for re-downloads)
      if (meta_event_id && meta_event_name) {
        trackMetaEvent(meta_event_name, meta_event_id);
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      
      // Handle security lock errors
      if (status === 423) {
        toast.error(data?.message || "Your account is temporarily locked. Please contact support.");
        return;
      }
      
      toast.error(data?.detail || data?.message || "Download failed");
    } finally {
      setLoading(false);
    }
  };

  if (variant === "full") {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleRedownload}
        disabled={loading}
        className="w-full gap-1.5 text-xs font-medium text-slate-600 hover:text-[#18181B] hover:bg-slate-50 border-slate-200"
        data-testid={`redownload-btn-${publicId}`}
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
        {loading ? "Downloading..." : "Re-download"}
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleRedownload}
      disabled={loading}
      className="h-8 w-8 shrink-0 text-slate-400 hover:text-[#18181B] hover:bg-slate-100"
      data-testid={`redownload-btn-${publicId}`}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" />
      )}
    </Button>
  );
}
